"use client";

import { Avatar, Button, Card } from "components/ui/v2";
import { PreviewCertificate } from "components/ui/v2/certificates/PreviewCertificate";
import { PublicPageLayoutV2 } from "components/ui/v2/public/PublicPageLayoutV2";
import { IPublicUserCertificateDetail } from "gql/Certificate-user/queries";
import { Award, ExternalLink } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useMemo } from "react";

export interface CertificatePublicPageV2Props {
  data: IPublicUserCertificateDetail;
}

function formatHighlightedText(
  template: string,
  values: Record<string, string>
): string {
  return template.replace(
    /{(\w+)}/g,
    (_, key: string) => `<strong>${values[key] ?? ""}</strong>`
  );
}

function formatIssueDate(issueDate: Date | string): string {
  return new Date(issueDate).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function resolveCourseUrl(courseUrl: string): string {
  return courseUrl.startsWith("https://") || courseUrl.startsWith("http://")
    ? courseUrl
    : `https://${courseUrl}`;
}

export function CertificatePublicPageV2({ data }: CertificatePublicPageV2Props) {
  const { t } = useTranslation("common");

  const fullName = useMemo(
    () => `${data.userName || ""} ${data.userLastName || ""}`.trim(),
    [data.userLastName, data.userName]
  );

  const recognitionHtml = useMemo(
    () =>
      formatHighlightedText(
        t("Queremos reconocer tu dedicación", {
          certificate: "{certificate}",
          duration: "{duration}",
        }),
        {
          certificate: data.footerTitle,
          duration: data.durationText,
        }
      ),
    [data.durationText, data.footerTitle, t]
  );

  const outcomes = data.learningOutcomes?.filter(Boolean) ?? [];
  const outcomesGridCols =
    outcomes.length === 2 || outcomes.length === 4
      ? "sm:grid-cols-2"
      : "sm:grid-cols-1";

  const openCourse = () => {
    if (!data.courseUrl) return;
    window.open(resolveCourseUrl(data.courseUrl), "_blank", "noopener,noreferrer");
  };

  return (
    <PublicPageLayoutV2 width="7xl" centered={false}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <header>
            <p className="text-sm font-semibold text-slate-500 dark:text-zinc-400">
              {t("certificado de participacion")}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white md:text-4xl">
              {data.certificateName}
            </h1>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-zinc-300 md:text-base">
              {t("felicitaciones por completar el programa")}
            </p>
          </header>

          <Card shadow="subtle" padding="comfortable">
            <div className="flex items-center gap-4">
              <Avatar
                src={data.profileImage}
                name={fullName || t("Usuario")}
                size="md"
              />
              <div className="min-w-0 space-y-1">
                <p className="truncate text-sm font-medium capitalize text-slate-900 dark:text-zinc-50">
                  {fullName}
                </p>
                <p className="text-sm text-slate-500 dark:text-zinc-400">
                  {data.durationText}
                </p>
                <p className="text-sm text-slate-500 dark:text-zinc-400">
                  {t("Finalizado el", {
                    certificateDate: formatIssueDate(data.issueDate),
                  })}
                </p>
              </div>
            </div>
          </Card>

          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <p
                className="break-words text-left text-sm leading-6 text-slate-600 dark:text-zinc-400"
                dangerouslySetInnerHTML={{ __html: recognitionHtml }}
              />
              <p className="text-left text-sm leading-6 text-slate-600 dark:text-zinc-400">
                {t("Este logro refleja no solo tu capacidad")}
              </p>
            </div>

            {outcomes.length > 0 ? (
              <Card shadow="subtle" padding="comfortable">
                <h2 className="text-base font-semibold leading-tight text-slate-950 dark:text-white">
                  {t("Aprendizajes claves del programa", {
                    name: data.footerTitle,
                  })}
                </h2>
                <ul
                  className={`mt-4 grid w-full grid-cols-1 gap-3 ${outcomesGridCols}`}
                >
                  {outcomes.map((outcome, index) => (
                    <li key={`${outcome}-${index}`} className="flex w-full items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
                        <Award className="h-4 w-4" />
                      </span>
                      <p className="min-w-0 flex-1 break-words text-left text-sm leading-6 text-slate-600 dark:text-zinc-400">
                        {outcome}
                      </p>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </div>
        </div>

        <div className="flex min-h-[600px] flex-col justify-center">
          <PreviewCertificate
            name={data.certificateName}
            duration={data.durationText}
            subtitle={data.certificateSubtitle}
            badge={data.badgeImage}
            certificateLabel={data.certificateLabel}
            vendorImage={data.vendorProfileImage}
            userName={data.userName}
            userLastName={data.userLastName}
            userProfileImage={data.profileImage}
            preview={false}
            issueDate={data.issueDate}
            certificateTopLabel={data.certificateTopLabel}
            userRole={data.userRole}
          />

          {data.allowViewCourse && data.titleTextMoreAboutCertificate ? (
            <div className="mt-5">
              <Card shadow="subtle" padding="comfortable">
                <div className="flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center">
                  <div className="min-w-0 space-y-2">
                    <h2 className="text-base font-semibold leading-tight text-slate-950 dark:text-white">
                      {data.titleTextMoreAboutCertificate}
                    </h2>
                    {data.textMoreAboutCertificate ? (
                      <p className="text-justify text-sm leading-6 text-slate-600 dark:text-zinc-400">
                        {data.textMoreAboutCertificate}
                      </p>
                    ) : null}
                  </div>
                  {data.buttonText ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="md"
                      onClick={openCourse}
                      aria-label={data.buttonText}
                      rightIcon={<ExternalLink className="h-4 w-4" />}
                    >
                      {data.buttonText}
                    </Button>
                  ) : null}
                </div>
              </Card>
            </div>
          ) : null}
        </div>
      </div>
    </PublicPageLayoutV2>
  );
}
